export interface ActivityDates {
  createdAt: string | null;
  updatedAt: string | null;
}

interface HasActivityDates {
  traces: { createdAt: string | null }[];
  createdAt?: string | null;
  updatedAt?: string | null;
}

/**
 * Activity dates for the admin deletion tab: the merge of the tool row's own
 * DB timestamps (worker >= 0.4.0 — always present, backfilled at migration) and
 * its trace dates. Earliest candidate wins `createdAt` (a backfilled row date can
 * postdate real machining), latest wins `updatedAt`. Null/unparsable dates are
 * ignored; both fields are null only when no source provides a date at all.
 */
export function toolActivityDates(tool: HasActivityDates): ActivityDates {
  // Collect first, reduce after — assigning from inside a helper closure defeats
  // TypeScript's narrowing (`tsc -b` rejects it; caught by the 1.16.1 CI build).
  const candidates: { time: number; raw: string }[] = [];
  const consider = (raw: string | null | undefined) => {
    if (!raw) return;
    const time = Date.parse(raw);
    if (Number.isNaN(time)) return;
    candidates.push({ time, raw });
  };

  for (const trace of tool.traces) consider(trace.createdAt);
  consider(tool.createdAt);
  consider(tool.updatedAt);

  let earliest: { time: number; raw: string } | null = null;
  let latest: { time: number; raw: string } | null = null;
  for (const candidate of candidates) {
    if (earliest === null || candidate.time < earliest.time) earliest = candidate;
    if (latest === null || candidate.time > latest.time) latest = candidate;
  }

  return {
    createdAt: earliest === null ? null : earliest.raw,
    updatedAt: latest === null ? null : latest.raw,
  };
}

/** Aggregate over a reference group: earliest created / latest updated across tools. */
export function groupActivityDates(tools: HasActivityDates[]): ActivityDates {
  const dates = tools.map(toolActivityDates);
  let created: { time: number; raw: string } | null = null;
  let updated: { time: number; raw: string } | null = null;
  for (const d of dates) {
    if (d.createdAt !== null) {
      const time = Date.parse(d.createdAt);
      if (created === null || time < created.time) created = { time, raw: d.createdAt };
    }
    if (d.updatedAt !== null) {
      const time = Date.parse(d.updatedAt);
      if (updated === null || time > updated.time) updated = { time, raw: d.updatedAt };
    }
  }
  return { createdAt: created?.raw ?? null, updatedAt: updated?.raw ?? null };
}
